import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCamera, FiX, FiCheck, FiAlertCircle, FiRefreshCw } from 'react-icons/fi';

const ScannerContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  max-width: 400px;
  width: 100%;
`;

const ScannerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 0.5rem;
`;

const ScannerTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0;
  transition: all 0.2s;
  
  &:hover {
    color: var(--text-primary);
    background: var(--bg-secondary);
  }
`;

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 300px;
  aspect-ratio: 1;
  border-radius: 0;
  overflow: hidden;
  background: var(--video-background, #000);
  border: 2px solid var(--border-color);
`;

const Video = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ScannerOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

const ScannerFrame = styled.div`
  width: 200px;
  height: 200px;
  border: 2px solid var(--primary-color);
  border-radius: 0;
  position: relative;
  
  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 20px;
    height: 20px;
    border: 3px solid var(--primary-color);
  }
  
  &::before {
    top: -3px;
    left: -3px;
    border-right: none;
    border-bottom: none;
  }
  
  &::after {
    bottom: -3px;
    right: -3px;
    border-left: none;
    border-top: none;
  }
`;

const ScannerInfo = styled.div`
  text-align: center;
  color: var(--text-secondary);
  font-size: 0.875rem;
  line-height: 1.4;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 0.5rem;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: var(--bg-secondary);
    border-color: var(--primary-color);
    transform: translateY(-1px);
  }
  
  &.primary {
    background: var(--primary-color);
    color: var(--text-inverse);
    border-color: var(--primary-color);
    
    &:hover {
      background: var(--primary-hover);
    }
  }
  
  &.success {
    background: rgba(104, 211, 145, 0.1);
    color: var(--success-dark);
    border-color: var(--success-color);
    
    &:hover {
      background: rgba(104, 211, 145, 0.2);
    }
  }
  
  &.error {
    background: rgba(252, 129, 129, 0.1);
    color: var(--error-color);
    border-color: var(--error-color);
    
    &:hover {
      background: rgba(252, 129, 129, 0.2);
    }
  }
`;


const Message = styled(motion.div)`
  padding: 0.75rem;
  border-radius: 0;
  font-size: 0.875rem;
  text-align: center;
  margin-top: 0.5rem;
  
  &.success {
    background: rgba(104, 211, 145, 0.1);
    border: 1px solid var(--success-color);
    color: var(--success-color);
  }
  
  &.error {
    background: rgba(252, 129, 129, 0.1);
    border: 1px solid var(--error-color);
    color: var(--error-color);
  }
  
  &.info {
    background: rgba(96, 165, 250, 0.1);
    border: 1px solid var(--info-color);
    color: var(--info-color);
  }
`;

const PermissionPrompt = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--text-secondary);
`;

const PermissionIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
`;

const PermissionTitle = styled.h3`
  color: var(--text-primary);
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
`;

const PermissionDescription = styled.p`
  color: var(--text-secondary);
  font-size: 0.875rem;
  line-height: 1.5;
  margin: 0 0 1.5rem 0;
`;

function QRCodeScanner({ 
  onScan, 
  onError, 
  onClose,
  title = "Scan QR Code",
  description = "Point your camera at the QR code to scan",
  isOpen = false 
}) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [hasPermission, setHasPermission] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const detectQRCode = useCallback(() => {
    if (!scanning || !videoRef.current) return;
    
    // Simple QR code detection using canvas
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const video = videoRef.current;
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    // For now, we'll use a simple approach
    // In a real implementation, you'd use a QR code detection library
    // like jsQR or qr-scanner
    
    // Continue scanning
    if (scanning) {
      requestAnimationFrame(detectQRCode);
    }
  }, [scanning]);

  const stopScanning = useCallback(() => {
    setScanning(false);
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const startScanning = useCallback(async () => {
    try {
      setError(null);
      setSuccess(null);
      
      // Request camera permission
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'environment', // Use back camera if available
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      });
      
      setHasPermission(true);
      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
      
      setScanning(true);
      
      // Start QR code detection
      detectQRCode();
      
    } catch (err) {
      console.error('Error accessing camera:', err);
      setHasPermission(false);
      setError('Camera access denied. Please allow camera permission to scan QR codes.');
      if (onError) onError(err);
    }
  }, [onError, detectQRCode]);

  useEffect(() => {
    if (isOpen) {
      startScanning();
    } else {
      stopScanning();
    }
    
    return () => {
      stopScanning();
    };
  }, [isOpen, startScanning, stopScanning]);

  const handleManualInput = () => {
    const input = prompt('Enter the attendance code manually:');
    if (input && input.trim()) {
      handleScanResult(input.trim());
    }
  };

  const handleScanResult = (data) => {
    setSuccess('QR code scanned successfully!');
    
    if (onScan) {
      onScan(data);
    }
    
    // Auto-close after successful scan
    setTimeout(() => {
      if (onClose) onClose();
    }, 2000);
  };

  const retryScanning = () => {
    setError(null);
    setSuccess(null);
    startScanning();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <ScannerContainer
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.2 }}
      >
        <ScannerHeader>
          <ScannerTitle>
            <FiCamera size={20} color="var(--primary-color)" />
            {title}
          </ScannerTitle>
          <CloseButton onClick={onClose}>
            <FiX size={20} />
          </CloseButton>
        </ScannerHeader>
        
        {hasPermission === false ? (
          <PermissionPrompt>
            <PermissionIcon>📷</PermissionIcon>
            <PermissionTitle>Camera Access Required</PermissionTitle>
            <PermissionDescription>
              To scan QR codes, please allow camera access in your browser settings.
            </PermissionDescription>
            <ActionButtons>
              <ActionButton onClick={retryScanning}>
                <FiRefreshCw size={16} />
                Try Again
              </ActionButton>
              <ActionButton onClick={handleManualInput} className="primary">
                <FiCheck size={16} />
                Enter Manually
              </ActionButton>
            </ActionButtons>
          </PermissionPrompt>
        ) : (
          <>
            <VideoContainer>
              <Video ref={videoRef} autoPlay playsInline muted />
              {scanning && (
                <ScannerOverlay>
                  <ScannerFrame />
                </ScannerOverlay>
              )}
            </VideoContainer>
            
            <ScannerInfo>{description}</ScannerInfo>
            
            {error && (
              <Message className="error" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <FiAlertCircle size={16} style={{ marginRight: '0.5rem' }} />
                {error}
              </Message>
            )}
            
            {success && (
              <Message className="success" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <FiCheck size={16} style={{ marginRight: '0.5rem' }} />
                {success}
              </Message>
            )}
            
            <ActionButtons>
              <ActionButton onClick={handleManualInput}>
                <FiCheck size={16} />
                Enter Manually
              </ActionButton>
              {error && (
                <ActionButton onClick={retryScanning}>
                  <FiRefreshCw size={16} />
                  Retry
                </ActionButton>
              )}
            </ActionButtons>
          </>
        )}
      </ScannerContainer>
    </AnimatePresence>
  );
}

export default QRCodeScanner;
